import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Snackbar,
  Grid,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import axios from 'axios';

import { API_ENDPOINTS } from '../../config';


// ✅ FIXED: Auth helper function
const getAuthConfig = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    },
    withCredentials: true
  };
};

function ApprovalRequests({ user, highlightedRequestId, clearHighlightedRequest }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  // ✅ Ref for scrolling to highlighted request
  const highlightedRowRef = useRef(null);
  
  // ✅ Track if we already switched tab for this highlighted request
  const [tabSwitchedForHighlight, setTabSwitchedForHighlight] = useState(false);

  // ✅ Handle highlighted request - auto switch to Pending tab (runs once when highlightedRequestId changes)
  useEffect(() => {
    if (highlightedRequestId && !tabSwitchedForHighlight) {
      // When coming from email link, switch to Pending tab (index 1)
      setActiveTab(1);
      setTabSwitchedForHighlight(true);
    }
  }, [highlightedRequestId, tabSwitchedForHighlight]);

  // ✅ Scroll to highlighted request when it becomes visible
  useEffect(() => {
    if (highlightedRequestId && highlightedRowRef.current && !loading) {
      setTimeout(() => {
        highlightedRowRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }, 300);
      
      // Clear highlight after 15 seconds
      const timer = setTimeout(() => {
        if (clearHighlightedRequest) {
          clearHighlightedRequest();
        }
      }, 15000);
      
      return () => clearTimeout(timer);
    }
  }, [highlightedRequestId, loading, requests, clearHighlightedRequest]);

  useEffect(() => {
    fetchRequests();
  }, [activeTab]);

  const fetchRequests = async () => {
    setLoading(true);
    setError('');
    
    try {
      const typeMap = ['all', 'pending', 'approved', 'rejected'];
      const type = typeMap[activeTab];
      
      console.log('Fetching approval requests, type:', type);
      
      const response = await axios.get(
        `${API_ENDPOINTS.APPROVALS}?type=${type}`,
        getAuthConfig()
      );

      console.log('Approval requests response:', response.data);

      if (response.data.success) {
        setRequests(response.data.requests || []);
      } else {
        setError(response.data.error || 'Failed to fetch approval requests');
      }
    } catch (err) {
      console.error('Error fetching approvals:', err);
      setError(err.response?.data?.error || err.message || 'Failed to fetch approval requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprovalAction = (request, action) => {
    console.log('Opening approval modal:', { request, action });
    setSelectedRequest(request);
    setModalAction(action);
    setComments('');
    setError('');
    setShowModal(true);
  };

  const handleViewDetails = (request) => {
    console.log('Viewing request details:', request);
    setSelectedRequest(request);
    setShowDetailsModal(true);
  };

  const handleConfirmAction = async () => {
    if (!selectedRequest) {
      console.error('No request selected');
      return;
    }

    console.log('Confirming action:', {
      request_id: selectedRequest.id,
      action: modalAction,
      comments: comments
    });

    setSubmitting(true);
    setError('');

    try {
      const payload = {
        request_id: selectedRequest.id,
        action: modalAction,
        comments: comments || ''
      };

      console.log('Sending PUT request:', payload);

      // ✅ FIXED: Now using getAuthConfig() for proper authentication
      const response = await axios.put(
        API_ENDPOINTS.APPROVALS,
        payload,
        getAuthConfig()  // ✅ Added proper auth config
      );

      console.log('Approval response:', response.data);

      if (response.data.success) {
        const actionText = modalAction === 'approve' ? 'approved' : 'rejected';
        setSuccessMessage(`✅ Request #${selectedRequest.id} ${actionText} successfully! Changes have been applied.`);
        setShowSuccess(true);
        setShowModal(false);
        setSelectedRequest(null);
        
        // Refresh the list after a short delay
        setTimeout(() => {
          fetchRequests();
        }, 1000);
      } else {
        setError(response.data.error || 'Failed to process request');
        console.error('Backend returned error:', response.data.error);
      }
    } catch (err) {
      console.error('Error processing approval:', err);
      console.error('Error response:', err.response);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to process request';
      setError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    if (!submitting) {
      setShowModal(false);
      setSelectedRequest(null);
      setComments('');
      setError('');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return 'warning';
      case 'APPROVED': return 'success';
      case 'REJECTED': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'URGENT': return 'error';
      case 'HIGH': return 'warning';
      case 'MEDIUM': return 'info';
      case 'LOW': return 'default';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const parseJsonSafely = (jsonString) => {
    try {
      return JSON.parse(jsonString);
    } catch (e) {
      return null;
    }
  };

  const renderChanges = (oldValues, newValues) => {
    const oldData = parseJsonSafely(oldValues);
    const newData = parseJsonSafely(newValues);
    
    if (!oldData || !newData) return null;

    const changes = [];
    Object.keys(newData).forEach(key => {
      if (oldData[key] !== newData[key] && !['id', 'nav_id', 'total_kgs', 'total_pounds'].includes(key)) {
        changes.push(
          <Box key={key} sx={{ mb: 2 }}>
            <Typography 
              variant="body1" 
              sx={{ 
                fontWeight: 'bold',
                fontSize: '1.1rem',
                color: '#1565c0',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                mb: 0.5
              }}
            >
              {key.replace(/_/g, ' ')}:
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1rem' }}>
              <span style={{ 
                textDecoration: 'line-through', 
                color: '#d32f2f',
                fontSize: '1.1rem',
                fontWeight: '500'
              }}>
                {String(oldData[key] || '-')}
              </span>
              {' → '}
              <span style={{ 
                color: '#2e7d32', 
                fontWeight: 'bold',
                fontSize: '1.1rem'
              }}>
                {String(newData[key] || '-')}
              </span>
            </Typography>
          </Box>
        );
      }
    });

    return changes.length > 0 ? <Box>{changes}</Box> : <Typography variant="body2">No changes detected</Typography>;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1565c0' }}>
          ✅ Approval Requests
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchRequests}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label={`All Requests (${requests.length})`} />
          <Tab label="Pending" />
          <Tab label="Approved" />
          <Tab label="Rejected" />
        </Tabs>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#1565c0' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>ID</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Type</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Requested By</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Inventory ID</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Reason</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Priority</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Created</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      No approval requests found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                requests.map((request) => (
                  <TableRow 
                    key={request.id} 
                    hover
                    ref={request.id === highlightedRequestId ? highlightedRowRef : null}
                    onClick={() => handleViewDetails(request)}
                    sx={{ 
                      cursor: 'pointer',
                      // ✅ Highlight animation for request from email link
                      ...(request.id === highlightedRequestId && {
                        backgroundColor: '#fff3cd !important',
                        animation: 'highlightPulse 2s ease-in-out infinite',
                        border: '3px solid #ffc107',
                        boxShadow: '0 0 20px rgba(255, 193, 7, 0.5)',
                        '& td': {
                          backgroundColor: 'transparent !important',
                          fontWeight: 'bold',
                        },
                        '@keyframes highlightPulse': {
                          '0%': { backgroundColor: '#fff3cd', boxShadow: '0 0 10px rgba(255, 193, 7, 0.3)' },
                          '50%': { backgroundColor: '#ffe69c', boxShadow: '0 0 25px rgba(255, 193, 7, 0.6)' },
                          '100%': { backgroundColor: '#fff3cd', boxShadow: '0 0 10px rgba(255, 193, 7, 0.3)' },
                        }
                      })
                    }}
                  >
                    <TableCell><strong>#{request.id}</strong></TableCell>
                    <TableCell>{request.change_type}</TableCell>
                    <TableCell>{request.requester_name || request.requester_username || 'Unknown'}</TableCell>
                    <TableCell>{request.inventory_id}</TableCell>
                    <TableCell>
                      <Tooltip title={request.reason || ''}>
                        <span>{(request.reason || '').substring(0, 50)}{request.reason?.length > 50 ? '...' : ''}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={request.status} 
                        color={getStatusColor(request.status)} 
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={request.priority} 
                        color={getPriorityColor(request.priority)} 
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem' }}>{formatDate(request.created_at)}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {request.status === 'PENDING' ? (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Approve">
                            <IconButton
                              color="success"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleApprovalAction(request, 'approve');
                              }}
                              sx={{
                                // ✅ Pulsing animation for highlighted request's approve button
                                ...(request.id === highlightedRequestId && {
                                  animation: 'pulseButton 1s ease-in-out infinite',
                                  backgroundColor: '#4caf50',
                                  color: 'white',
                                  '&:hover': {
                                    backgroundColor: '#388e3c',
                                  },
                                  '@keyframes pulseButton': {
                                    '0%': { transform: 'scale(1)' },
                                    '50%': { transform: 'scale(1.2)' },
                                    '100%': { transform: 'scale(1)' },
                                  }
                                })
                              }}
                            >
                              <ApproveIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton
                              color="error"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleApprovalAction(request, 'reject');
                              }}
                            >
                              <RejectIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {request.status} by {request.approver_name || 'Admin'}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Details Modal */}
      <Dialog 
        open={showDetailsModal} 
        onClose={() => setShowDetailsModal(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#1565c0', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>📋 Request Details #{selectedRequest?.id}</span>
          <Chip 
            label={selectedRequest?.status} 
            color={getStatusColor(selectedRequest?.status)} 
            size="small"
            sx={{ color: 'white', fontWeight: 'bold' }}
          />
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedRequest && (
            <Box>
              {/* Request Information */}
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#1565c0', fontWeight: 'bold' }}>
                 ℹ️ Request Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Request ID:</Typography>
                    <Typography variant="body1" fontWeight="bold">#{selectedRequest.id}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Request Type:</Typography>
                    <Typography variant="body1" fontWeight="bold">{selectedRequest.change_type}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Inventory ID:</Typography>
                    <Typography variant="body1" fontWeight="bold">#{selectedRequest.inventory_id}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Priority:</Typography>
                    <Chip label={selectedRequest.priority} color={getPriorityColor(selectedRequest.priority)} size="small" />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Created:</Typography>
                    <Typography variant="body2">{formatDate(selectedRequest.created_at)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Status:</Typography>
                    <Chip label={selectedRequest.status} color={getStatusColor(selectedRequest.status)} size="small" />
                  </Grid>
                </Grid>
              </Paper>

              {/* Requester Information */}
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#e3f2fd' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#1565c0', fontWeight: 'bold' }}>
                  👤 Requested By
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {selectedRequest.requester_name || selectedRequest.requester_username || 'Unknown User'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedRequest.ip_address && `IP: ${selectedRequest.ip_address}`}
                </Typography>
              </Paper>

              {/* Reason */}
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fff3e0' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#f57c00', fontWeight: 'bold' }}>
                  📝 Reason for Request
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedRequest.reason || 'No reason provided'}
                </Typography>
              </Paper>

              {/* Changes Requested */}
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                  🔄 Requested Changes
                </Typography>
                {renderChanges(selectedRequest.old_values, selectedRequest.new_values)}
              </Paper>

              {/* Approval Information (if approved/rejected) */}
              {selectedRequest.status !== 'PENDING' && (
                <Paper variant="outlined" sx={{ p: 2, bgcolor: selectedRequest.status === 'APPROVED' ? '#e8f5e9' : '#ffebee' }}>
                  <Typography variant="h6" gutterBottom sx={{ 
                    color: selectedRequest.status === 'APPROVED' ? '#2e7d32' : '#d32f2f', 
                    fontWeight: 'bold' 
                  }}>
                    {selectedRequest.status === 'APPROVED' ? '✅ Approval Details' : '❌ Rejection Details'}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        {selectedRequest.status === 'APPROVED' ? 'Approved By:' : 'Rejected By:'}
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {selectedRequest.approver_name || selectedRequest.approver_username || 'Admin'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        {selectedRequest.status === 'APPROVED' ? 'Approved At:' : 'Rejected At:'}
                      </Typography>
                      <Typography variant="body2">{formatDate(selectedRequest.approved_at)}</Typography>
                    </Grid>
                    {selectedRequest.rejection_reason && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">Comments:</Typography>
                        <Typography variant="body2">{selectedRequest.rejection_reason}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          {selectedRequest?.status === 'PENDING' && (
            <>
              <Button
                variant="contained"
                color="success"
                startIcon={<ApproveIcon />}
                onClick={() => {
                  setShowDetailsModal(false);
                  handleApprovalAction(selectedRequest, 'approve');
                }}
              >
                Approve
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<RejectIcon />}
                onClick={() => {
                  setShowDetailsModal(false);
                  handleApprovalAction(selectedRequest, 'reject');
                }}
              >
                Reject
              </Button>
            </>
          )}
          <Button onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approval/Rejection Modal */}
      <Dialog 
        open={showModal} 
        onClose={handleCloseModal} 
        maxWidth="md" 
        fullWidth
        disableEscapeKeyDown={submitting}
      >
        <DialogTitle sx={{ 
          bgcolor: modalAction === 'approve' ? '#4caf50' : '#f44336', 
          color: 'white' 
        }}>
          {modalAction === 'approve' ? '✓ Approve' : '✗ Reject'} Request #{selectedRequest?.id}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {selectedRequest && (
            <Box>
              {/* Request Info */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Type:</Typography>
                  <Chip 
                    label={selectedRequest.change_type} 
                    size="small" 
                    color={selectedRequest.change_type === 'EDIT' ? 'primary' : selectedRequest.change_type === 'DELETE' ? 'error' : 'info'}
                    sx={{ fontWeight: 700 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Requested by:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {selectedRequest.requester_name || selectedRequest.requester_username}
                  </Typography>
                </Grid>
              </Grid>

              {/* Inventory Context - Show what item this is about */}
              {(() => {
                const oldData = parseJsonSafely(selectedRequest.old_values);
                if (!oldData) return null;
                
                return (
                  <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#e3f2fd', borderColor: '#1565c0' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1565c0', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                      📦 Inventory Item Details
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">Product</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{oldData.product || '-'}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">Grade</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{oldData.grade || '-'}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">Pack Size</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{oldData.pack_size || '-'}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">Brand</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{oldData.brand || '-'}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">Pallet ID</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{oldData.pallet_id || '-'}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">Location (CS1/CS2)</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {oldData.cs1 || '-'} / {oldData.cs2 || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">Status</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{oldData.status || '-'}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">Shift</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{oldData.shift || '-'}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">NAV ID</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: oldData.nav_id === 'NOT AVAILABLE' ? '#f44336' : '#2e7d32' }}>
                          {oldData.nav_id || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">Cartons</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{oldData.cartons || '0'}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">Loose Bags</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{oldData.loose_bags || '0'}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">Trace ID</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{oldData.trace_id || '-'}</Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                );
              })()}

              {/* Reason/Description */}
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Reason for Change:
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, fontStyle: 'italic', bgcolor: '#fff3e0', p: 1.5, borderRadius: 1 }}>
                "{selectedRequest.reason}"
              </Typography>

              {/* Requested Changes */}
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#d32f2f', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                ✏️ Requested Changes:
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fff8e1', borderColor: '#ff9800' }}>
                {renderChanges(selectedRequest.old_values, selectedRequest.new_values)}
              </Paper>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Approver Comments (optional):"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Add any comments about this decision..."
                disabled={submitting}
                sx={{ mb: 2 }}
              />

              <Alert severity="warning" icon={<span>⚠️</span>}>
                This action will permanently apply the requested changes to the inventory.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5' }}>
          <Button 
            onClick={handleCloseModal} 
            disabled={submitting}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmAction}
            disabled={submitting}
            sx={{
              bgcolor: modalAction === 'approve' ? '#4caf50' : '#f44336',
              '&:hover': {
                bgcolor: modalAction === 'approve' ? '#388e3c' : '#d32f2f'
              },
              minWidth: 180
            }}
            startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {submitting 
              ? 'Processing...' 
              : `✓ Confirm ${modalAction === 'approve' ? 'Approval' : 'Rejection'}`
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowSuccess(false)} 
          severity="success" 
          variant="filled"
          sx={{ width: '100%', fontSize: '1.1rem', fontWeight: 'bold' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ApprovalRequests;